import { inject, reactive } from 'vue'

const ROUTER_KEY = Symbol('router')
const ROUTE_KEY = Symbol('route')

function resolvePathFromLocation(location, routes) {
  if (typeof location === 'string') {
    return location
  }

  if (location && location.path) {
    return location.path
  }

  if (location && location.name) {
    const record = routes.find(route => route.name === location.name)
    return record ? record.path : '/'
  }

  return '/'
}

function extractParamsForRoute(path, route) {
  const params = {}
  const routeSegments = (route.path || '').split('/').filter(Boolean)
  const pathSegments = (path || '').split('/').filter(Boolean)

  for (let i = 0, j = 0; i < routeSegments.length; i += 1, j += 1) {
    const routeSegment = routeSegments[i]
    const isParam = routeSegment.startsWith(':')
    const hasCatchAll = routeSegment.includes('(.*)')
    const pathSegment = pathSegments[j]

    if (isParam) {
      const paramName = routeSegment.replace(/^:/, '').replace(/\(.*\)\*?/, '')

      if (hasCatchAll) {
        params[paramName] = pathSegments.slice(j).join('/')
        return { record: route, params }
      }

      if (typeof pathSegment === 'undefined') {
        return null
      }

      params[paramName] = pathSegment
      continue
    }

    if (routeSegment !== pathSegment) {
      return null
    }
  }

  // Ensure exact length match when there is no catch-all segment
  const lastRouteSegment = routeSegments[routeSegments.length - 1] || ''
  if (!lastRouteSegment.includes('(.*)') && routeSegments.length !== pathSegments.length) {
    return null
  }

  return { record: route, params }
}

function matchRoute(path, routes) {
  for (const route of routes) {
    const matched = extractParamsForRoute(path, route)
    if (matched) {
      return matched
    }
  }

  const catchAll = routes.find(route => route.path === '/:pathMatch(.*)*')
  if (catchAll) {
    return {
      record: catchAll,
      params: { pathMatch: (path || '').replace(/^\//, '') }
    }
  }

  const fallback = routes.find(route => route.path === '*')

  return { record: fallback || routes[0], params: {} }
}

function createRouteState(record, path, params = {}) {
  return {
    fullPath: path,
    path,
    name: record?.name || '',
    meta: record?.meta || {},
    params: params || {}
  }
}

export function createRouter({ routes = [] }) {
  const state = reactive(createRouteState(routes[0], '/', {}))

  const syncWithPath = (path, replace = false) => {
    const matched = matchRoute(path, routes)
    const record = matched?.record

    if (record && record.redirect) {
      const target = typeof record.redirect === 'function' ? record.redirect() : record.redirect
      if (target && target !== path) {
        syncWithPath(target, replace)
      }
      return
    }

    const params = matched?.params || {}

    state.fullPath = path
    state.path = path
    state.name = record?.name || ''
    state.meta = record?.meta || {}
    state.params = params

    if (typeof window !== 'undefined') {
      const navigationMethod = replace ? 'replaceState' : 'pushState'
      window.history[navigationMethod]({}, '', path)
    }
  }

  const router = {
    currentRoute: state,
    push(location) {
      const path = resolvePathFromLocation(location, routes)
      syncWithPath(path, false)
    },
    replace(location) {
      const path = resolvePathFromLocation(location, routes)
      syncWithPath(path, true)
    },
    install(app) {
      if (typeof window !== 'undefined') {
        syncWithPath(window.location.pathname || '/', true)
        window.addEventListener('popstate', () => syncWithPath(window.location.pathname || '/', true))
      }

      app.provide(ROUTER_KEY, router)
      app.provide(ROUTE_KEY, state)
      app.config.globalProperties.$router = router
      app.config.globalProperties.$route = state
    }
  }

  return router
}

export function createWebHistory() {
  return {}
}

export function useRouter() {
  return inject(ROUTER_KEY)
}

export function useRoute() {
  return inject(ROUTE_KEY)
}
